import { FC, useEffect, useState } from "react";
import {
    Box, Button, Divider, MenuItem, FormControl, Grid,
    IconButton, InputAdornment, InputLabel, LinearProgress,
    Paper, Select, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, styled,
    tableCellClasses, Pagination, Container, Typography, Backdrop, CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ClearIcon from "@mui/icons-material/Clear";
import { tableStyle } from "../../../Pays/Pays";
import UserForm from "../../../../../forms/UserForm";
import { DataRef } from "../../../../../../types/DataRef";
import { SnackBarAlert } from "../../../../../element/SnackBarAlert";
import { AlertType } from "../../../../../../types/AlertType";
import UtilisateurRow from "./UtilisateurRow";
import { DataProfil } from "../../../../../../types/DataTypes";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loggoutUser } from "../../../../../../redux/actions/logginUser";
import CheckAttr from "../../../../../../utils/CheckAttr";
import OptionalWidthModal from "../../../../../../commons/optional-width-modal/OptionalWidthModal";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        textAlign: 'center'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
        textAlign: 'center',
        color: "#fff"
    },
}));

export interface DataUtilisateur {
    id?: number;
    name: string;
    email: string;
    prenom: string;
    actif: string;
    Departement?: string;
    pays_id?: number;
    pays: DataRef[];
    profils: DataRef[];
    token?: string;
    features?: string[];
    failed_attempts: number;
    updated_password: boolean;
    multiProfile?: boolean;
    allProfil?: DataRef[];
    devise_id?: number
}

interface UtilisateurProps {
    isDesktopOrLaptop: boolean;
}

const initialiseSearchInputValidation = {
    filter: false,
    keyword: false
};

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const Utilisateur: FC<UtilisateurProps> = ({ isDesktopOrLaptop }) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [openSnack, setOpenSnack] = useState<boolean>(false);
    const [refresh, setRefresh] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<number | false>(false);

    const [page, setPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(5);
    const [totalData, setTotalData] = useState<number>(15);
    const [filter, setFilter] = useState<string>('');
    const [keyword, setKeyword] = useState<string>('');

    const [alertMessage, setAlerteMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<AlertType>("success");

    const [dataPays, setDataPays] = useState<DataRef[]>([]);
    const [dataProfils, setDataProfils] = useState<DataProfil[]>([]);
    const [dataUsers, setDataUsers] = useState<DataUtilisateur[]>([]);

    const [errorSearch, setErrorSearchInput] = useState<any>(initialiseSearchInputValidation);

    const cookies = new Cookies();
    const token: string = cookies.get("jwt");

    const http: AxiosInstance = axios.create({
        baseURL: urlBaseApi,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    const navigate = useNavigate();
    const dispatch = useDispatch();


    // Add global response interceptor
    http.interceptors.response.use(
        (response) => {
            // Modify response data here, if needed
            return response;
        },
        (error) => {
            console.log(error.message);
            if (error.message === "Request failed with status code 401") {
                // Remove the redux store
                dispatch(loggoutUser());

                // Remove token in navigator cookies
                cookies.remove('jwt', { path: '/' });

                navigate("/sign-in");
            }
            return Promise.reject(error);
        }
    );

    const receiveData = (initial: boolean = false) => {
        // Récupère les utilisateurs
        setIsLoading(true);
        const data = {
            filter,
            keyword: initial ? "" : keyword,
            rowsPerPage,
            page
        };

        http.post('user/list', data)
            .then(res => {
                const { total, data } = res.data;

                setTotalData(total);
                Array.isArray(data) && setDataUsers(data);
            }).catch(error => {
                console.log(error);
            }).finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        // Récupère les utilisateurs
        receiveData();
    }, [rowsPerPage, page, refresh]);

    useEffect(() => {
        // Récupère les pays
        http.get('getallpays')
            .then(res => {
                Array.isArray(res.data) && setDataPays(res.data);
            }).catch(error => {
                console.log(error);
            });

        // Récupère les profils
        http.get('profil/find/all')
            .then(res => {
                Array.isArray(res.data) && setDataProfils(res.data);
            }).catch(error => {
                console.log(error);
            });
    }, []);

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        name === "filter" && setFilter(value);
        name === "keyword" && setKeyword(value);

        value === "" && receiveData(true);
    };

    const handleSearch = (): void => {
        // reinitialiser à false les valeurs des attributs qui controle les champs obligatoire
        setErrorSearchInput((prev: any) => ({
            ...prev,
            filter: false,
            keyword: false,
        }));

        if (filter !== '' && keyword !== '') {
            setPage(1);
            receiveData();
        } else {
            if (filter === '') {
                setErrorSearchInput((prev: any) => ({ ...prev, filter: true }));
            }
            if (keyword === '') {
                setErrorSearchInput((prev: any) => ({ ...prev, keyword: true }));
            }
            // showMessageWarning("SVP! Merci de remplir les Champs de recherche.");
        }
    };

    const handleClose = () => {
        setOpenModal(false);
    }

    const handleCloseAlert = () => {
        setOpenSnack(false)
    }

    const reloadList = () => {
        setRefresh(!refresh);
    };

    const resetFilter = () => {
        setPage(1);
        setRowsPerPage(5);
        setFilter("");
        setKeyword("");
        reloadList();
    };

    const pagination: JSX.Element | boolean = !isLoading &&
        <Stack spacing={2} direction="row">
            <InputLabel sx={{ marginTop: "8px", color: "#fff" }}>Lignes par page :</InputLabel>
            <FormControl variant="standard" sx={{ minWidth: 50 }}>
                <Select
                    labelId="demo-simple-select-standard-label"
                    id="demo-simple-select-standard"
                    color="warning"
                    sx={{ background: "#fff", borderRadius: 1, paddingLeft: 1 }}
                    value={rowsPerPage}
                    onChange={(e) => setRowsPerPage(e.target.value as number)}
                    defaultValue={5}
                >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                </Select>
            </FormControl>
            {
                totalData > rowsPerPage && (
                    <Pagination
                        count={Math.ceil(totalData / rowsPerPage)}
                        color="standard"
                        sx={{ button: { color: "white" } }}
                        showFirstButton={true}
                        showLastButton={true}
                        siblingCount={0}
                        boundaryCount={1}
                        defaultPage={page}
                        onChange={(event, value) => setPage(value)}
                    />)
            }
        </Stack>;

    const stackAlert: JSX.Element = SnackBarAlert(openSnack, handleCloseAlert, alertType, alertMessage);

    return (
        <Container maxWidth="xl">
            {isDesktopOrLaptop ? (
                <Box mt={3}>
                    <Grid container spacing={2} columns={{ xs: 4, sm: 9, md: 12 }}>
                        <Grid item xs={4} sm={3} md={6}>
                            <Button
                                variant="contained"
                                startIcon={<PersonAddIcon />}
                                color="warning"
                                sx={{ textTransform: "none" }}
                                onClick={() => setOpenModal(true)}
                            >
                                Ajouter
                            </Button>
                        </Grid>
                        <Grid item xs={4} sm={6} md={6}>
                            <Stack
                                direction="row"
                                spacing={2}
                                display="flex"
                                justifyContent="end"
                            >
                                <FormControl sx={{ minWidth: 200 }}>
                                    <InputLabel id="demo-simple-select-label" color="warning" sx={{ mt: -1 }}>Chercher par ...</InputLabel>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        name="filter"
                                        sx={{ background: "#fff" }}
                                        size="small"
                                        color="warning"
                                        label="Age"
                                        value={filter}
                                        error={errorSearch?.filter}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value=""></MenuItem>
                                        <MenuItem value="name">Nom</MenuItem>
                                        <MenuItem value="prenom">Prénom</MenuItem>
                                        <MenuItem value="email">Email</MenuItem>
                                        <MenuItem value="profil">Profil</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    sx={{ width: 200, backgroundColor: "#fff", borderRadius: 1 }}
                                    name="keyword"
                                    placeholder="Taper quelque chose ..."
                                    size="small"
                                    color="warning"
                                    autoComplete="off"
                                    value={keyword}
                                    error={errorSearch?.keyword}
                                    onChange={handleChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="search"
                                                    edge="end"
                                                    onClick={handleSearch}
                                                >
                                                    <SearchIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Stack>
                        </Grid>
                    </Grid>

                    <Box display='flex' justifyContent='left' mt={4}>
                        {pagination}
                        <Divider light />
                    </Box>
                    <TableContainer component={Paper} style={tableStyle}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>
                                        Nom
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        Prenom
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        Email
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        Pays
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        Departement
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        Profil
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        Statut
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        Actions
                                    </StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {totalData > 0 ? (
                                    dataUsers.map((user) => (
                                        <UtilisateurRow
                                            key={user?.id}
                                            isDesktopOrLaptop={isDesktopOrLaptop}
                                            utilisateur={user}
                                            dataPays={dataPays}
                                            dataProfils={dataProfils}
                                            expanded={expanded}
                                            setExpanded={setExpanded}
                                            reloadList={reloadList}
                                            setAlertType={setAlertType}
                                            setOpenSnack={setOpenSnack}
                                            setAlerteMessage={setAlerteMessage}
                                        />
                                    ))
                                ) : (
                                    <StyledTableCell colSpan={7}>
                                        Aucun resultat a été trouvé ...
                                    </StyledTableCell>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box display='flex' justifyContent='right' mt={2}>
                        {pagination}
                    </Box>
                </Box>
            ) : (
                <>
                    <Box sx={{
                        position: 'fixed',
                        width: '95%',
                        top: '14.71rem',
                        pr: 7,
                        pt: 2,
                        bgcolor: '#080d24',
                        zIndex: 1000
                    }}>
                        <Button
                            variant="contained"
                            size='small'
                            startIcon={<PersonAddIcon />}
                            color="warning"
                            fullWidth
                            sx={{ textTransform: "none" }}
                            onClick={() => setOpenModal(true)}
                        >
                            Ajouter
                        </Button>

                        <FormControl sx={{ mt: 2.5 }} fullWidth>
                            <InputLabel
                                id="demo-simple-select-label"
                                color="warning"
                                sx={{ mt: -1 }}
                            >
                                {!CheckAttr(filter) && "Chercher par ..."}
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                sx={{ background: "#fff" }}
                                size="small"
                                color="warning"
                                label="Chercher par ..."
                                name="filter"
                                value={filter}
                                error={errorSearch?.filter}
                                onChange={handleChange}
                            >
                                <MenuItem value=""></MenuItem>
                                <MenuItem value="name">Nom</MenuItem>
                                <MenuItem value="prenom">Prénom</MenuItem>
                                <MenuItem value="email">Email</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            id="outlined-basic"
                            variant="outlined"
                            sx={{
                                bgcolor: "#fff",
                                borderRadius: 1,
                                mt: 0.5,
                                mb: 3
                            }}
                            placeholder="Taper quelque chose ..."
                            size="small"
                            fullWidth
                            name="keyword"
                            value={keyword}
                            error={errorSearch?.keyword}
                            autoComplete="off"
                            onChange={handleChange}
                            color="warning"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            type="button"
                                            aria-label="search"
                                            onClick={() => handleSearch()}
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                        <IconButton
                                            aria-label="refresh"
                                            onClick={() => resetFilter()}
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Typography>Liste des Utilisateurs</Typography>
                        <Divider orientation="horizontal" sx={{ bgcolor: "#fff", mb: 2 }} />

                        {!isLoading && totalData > rowsPerPage && (
                            <Box display="flex" justifyContent="end">
                                <Pagination
                                    count={Math.ceil(totalData / rowsPerPage)}
                                    size="small"
                                    color="standard"
                                    sx={{ button: { color: "white" }, mb: 2 }}
                                    showFirstButton={true}
                                    showLastButton={true}
                                    siblingCount={0}
                                    boundaryCount={1}
                                    defaultPage={page}
                                    onChange={(event, value) => setPage(value)}
                                />
                            </Box>
                        )}
                    </Box>

                    <Box mt='16.2rem'>
                        {dataUsers.map((user) => (
                            <UtilisateurRow
                                key={user?.id}
                                isDesktopOrLaptop={isDesktopOrLaptop}
                                utilisateur={user}
                                dataPays={dataPays}
                                dataProfils={dataProfils}
                                expanded={expanded}
                                setExpanded={setExpanded}
                                reloadList={reloadList}
                                setAlertType={setAlertType}
                                setOpenSnack={setOpenSnack}
                                setAlerteMessage={setAlerteMessage}
                            />
                        ))}
                    </Box>

                    <Stack direction="row" sx={{ mt: 3, mb: 4 }}>
                        <InputLabel sx={{ color: "#fff", fontSize: 12, mt: 0.5 }}>
                            Lignes par page :
                        </InputLabel>
                        <FormControl variant="standard" sx={{ minWidth: 48 }}>
                            <Select
                                labelId="demo-simple-select-standard-label"
                                id="demo-simple-select-standard"
                                color="warning"
                                sx={{
                                    background: "#fff",
                                    borderRadius: 1,
                                    paddingLeft: 1,
                                    paddingTop: 0.5,
                                    marginLeft: 1,
                                    fontSize: 12,
                                }}
                                value={rowsPerPage}
                                onChange={(e) => setRowsPerPage(e.target.value as number)}
                                defaultValue={5}
                                size="small"
                            >
                                <MenuItem value={5}>5</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </>
            )}

            <OptionalWidthModal
                title="Ajouter une nouvelle utilisateur"
                breackPoint="md"
                onClose={handleClose}
                open={openModal}
            >
                <UserForm
                    dataPays={dataPays}
                    dataProfils={dataProfils}
                    onClose={handleClose}
                    setOpenSnack={setOpenSnack}
                    setAlerteMessage={setAlerteMessage}
                    setAlertType={setAlertType}
                    reloadList={reloadList}
                />
            </OptionalWidthModal>

            <div>
                <Backdrop
                    sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={isLoading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </div>

            {stackAlert}
        </Container>
    );
};

export default Utilisateur;